<template>
  <div class="blocked-users-page">
    <!-- Header -->
    <div class="blocked-users-header">
      <button class="back-btn" @click="$router.back()">
        <i class="bi bi-arrow-right"></i>
      </button>
      <h1 class="blocked-users-title">
        {{ $t("chat.blocked_users") || "Blocked Users" }}
      </h1>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="blocked-users-loading">
      <div class="spinner-border text-success" role="status"></div>
      <span>{{ $t("common.loading") || "Loading..." }}</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="blocked-users-error">
      <i class="bi bi-exclamation-triangle"></i>
      <p>{{ error }}</p>
      <button class="btn btn-success" @click="loadBlockedUsers">
        {{ $t("common.retry") || "Retry" }}
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="blockedUsers.length === 0" class="blocked-users-empty">
      <i class="bi bi-slash-circle"></i>
      <h3>{{ $t("chat.no_blocked_users") || "No blocked users" }}</h3>
      <p>
        {{
          $t("chat.no_blocked_users_desc") || "Users you block will appear here"
        }}
      </p>
    </div>

    <!-- Blocked Users List -->
    <div v-else class="blocked-users-content">
      <div
        v-for="user in blockedUsers"
        :key="user.id || user.conversation_id"
        class="blocked-user-item"
      >
        <div class="blocked-user-avatar">
          <img
            v-if="user.image || user.photo_url"
            :src="user.image || user.photo_url"
            @error="onAvatarError"
            alt=""
          />
          <div v-else class="blocked-user-avatar-placeholder">
            {{ getInitial(user.name) }}
          </div>
        </div>
        <div class="blocked-user-info">
          <div class="blocked-user-name">{{ user.name }}</div>
          <div v-if="user.blocked_at" class="blocked-user-date">
            {{ formatDate(user.blocked_at) }}
          </div>
        </div>
        <div class="blocked-user-actions">
          <div class="dropdown">
            <button class="blocked-user-menu-btn" data-bs-toggle="dropdown">
              <i class="bi bi-three-dots-vertical"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li>
                <button class="dropdown-item" @click="showUnblockDialog(user)">
                  <i class="bi bi-unlock me-2"></i>
                  {{ $t("chat.unblock_user") || "Unblock User" }}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Unblock User Confirmation Modal -->
    <transition name="fade">
      <div
        v-if="showUnblockModal"
        class="modal-overlay"
        @click="closeUnblockModal"
      >
        <div class="confirm-modal" @click.stop>
          <div class="confirm-modal__icon confirm-modal__icon--success">
            <i class="bi bi-unlock"></i>
          </div>
          <h3 class="confirm-modal__title">
            {{ $t("chat.unblock_user") || "Unblock User" }}
          </h3>
          <p class="confirm-modal__text">
            {{
              $t("chat.unblock_user_confirmation") ||
              "Are you sure you want to unblock this user?"
            }}
          </p>
          <div class="confirm-modal__actions">
            <button
              class="confirm-modal__btn confirm-modal__btn--cancel"
              @click="closeUnblockModal"
              :disabled="actionLoading"
            >
              {{ $t("common.cancel") || "Cancel" }}
            </button>
            <button
              class="confirm-modal__btn confirm-modal__btn--success"
              @click="confirmUnblock"
              :disabled="actionLoading"
            >
              <span
                v-if="actionLoading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              {{ $t("chat.unblock") || "Unblock" }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ChatService } from "@/services/chat";

export default {
  name: "BlockedUsers",
  data() {
    return {
      blockedUsers: [],
      loading: true,
      error: null,
      showUnblockModal: false,
      selectedUser: null,
      actionLoading: false,
    };
  },
  mounted() {
    this.loadBlockedUsers();
  },
  methods: {
    async loadBlockedUsers() {
      this.loading = true;
      this.error = null;
      try {
        this.blockedUsers = await ChatService.getBlockedUsers();
      } catch (e) {
        this.error =
          this.$t("chat.failed_to_load_blocked_users") ||
          "Failed to load blocked users";
      } finally {
        this.loading = false;
      }
    },
    getInitial(name) {
      return name ? name.charAt(0).toUpperCase() : "?";
    },
    onAvatarError(e) {
      e.target.style.display = "none";
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString();
      } catch {
        return "";
      }
    },
    showUnblockDialog(user) {
      this.selectedUser = user;
      this.showUnblockModal = true;
    },
    closeUnblockModal() {
      this.showUnblockModal = false;
      this.selectedUser = null;
    },
    async confirmUnblock() {
      if (!this.selectedUser || !this.selectedUser.conversation_id) return;

      this.actionLoading = true;
      try {
        const result = await ChatService.unblockUser(
          this.selectedUser.conversation_id
        );
        if (result.success !== false) {
          this.closeUnblockModal();
          await this.loadBlockedUsers();
          this.$toast?.success?.(
            this.$t("chat.user_unblocked_successfully") ||
              "User unblocked successfully"
          );
        } else {
          this.$toast?.error?.(
            result.message ||
              this.$t("chat.failed_to_unblock_user") ||
              "Failed to unblock user"
          );
        }
      } catch (e) {
        this.$toast?.error?.(
          this.$t("chat.failed_to_unblock_user") || "Failed to unblock user"
        );
      } finally {
        this.actionLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.blocked-users-page {
  min-height: 100vh;
  background: var(--bs-body-bg);
}

.blocked-users-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: var(--bs-card-bg);
  border-bottom: 1px solid var(--bs-border-color);
  position: sticky;
  top: 0;
  z-index: 10;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #00c444;
  color: #ffffff !important;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}

.back-btn i,
.back-btn svg {
  color: #ffffff !important;
  fill: #ffffff !important;
}

.back-btn:hover {
  background: #00a838;
}

.blocked-users-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.blocked-users-loading,
.blocked-users-error,
.blocked-users-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: var(--bs-secondary);
}

.blocked-users-loading .spinner-border {
  margin-bottom: 1rem;
}

.blocked-users-error i,
.blocked-users-empty i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.blocked-users-empty h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--bs-body-color);
}

.blocked-users-empty p {
  max-width: 300px;
}

.blocked-users-content {
  padding: 1rem;
}

.blocked-user-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--bs-card-bg);
  border-radius: 12px;
  margin-bottom: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.blocked-user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.blocked-user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.blocked-user-avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.25rem;
}

.blocked-user-info {
  flex: 1;
  min-width: 0;
}

.blocked-user-name {
  font-weight: 600;
  color: var(--bs-body-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.blocked-user-date {
  font-size: 0.875rem;
  color: var(--bs-secondary);
}

.blocked-user-menu-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--bs-secondary);
  cursor: pointer;
  transition: background 0.2s;
}

.blocked-user-menu-btn:hover {
  background: var(--bs-gray-200);
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.confirm-modal {
  background: var(--bs-card-bg);
  border-radius: 16px;
  padding: 2rem;
  max-width: 400px;
  width: 100%;
  text-align: center;
}

.confirm-modal__icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 1.75rem;
}

.confirm-modal__icon--success {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.confirm-modal__title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.confirm-modal__text {
  color: var(--bs-secondary);
  margin-bottom: 1.5rem;
}

.confirm-modal__actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
}

.confirm-modal__btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.confirm-modal__btn--cancel {
  background: var(--bs-gray-200);
  color: var(--bs-body-color);
}

.confirm-modal__btn--cancel:hover {
  background: var(--bs-gray-300);
}

.confirm-modal__btn--success {
  background: #10b981;
  color: white;
}

.confirm-modal__btn--success:hover {
  background: #059669;
}

.confirm-modal__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
